// ─── WhepPlayer ───────────────────────────────────────────────────────────────
// Cliente WHEP — pide grant al backend, negocia SDP con el sidecar M4 vía
// Tailscale, renderiza `<video>` con el stream sub-segundo.
//
// El video viaja DIRECT browser ↔ M4 sobre WireGuard (Tailscale). El backend
// solo orquesta el grant + verify. Cero bytes de video por el cloud (excepto
// si ICE direct falla y cae al TURN relay de Hetzner).
//
// Props:
//   siteId        — id del sitio (para el grant endpoint)
//   channel       — canal numérico de la cámara
//   channelName   — label que se muestra abajo a la izquierda
//   token         — JWT del operador
//   onFallback    — callback opcional cuando el usuario click "Usar MJPEG"
//   compact       — si true, oculta el overlay de error con botón fallback
//                   (útil en cells de grid donde no hay espacio).
//   posterUrl     — (opcional, sprint Performance Live 2026-06-21) URL del
//                   snapshot del NVR que se muestra como poster del <video>
//                   mientras la conexión WebRTC se establece. Hace que el
//                   operador vea "algo" en 200ms en vez de un negro de 1-3s.

import { useEffect, useRef, useState } from 'react'
import { streamPool } from '../lib/streamPool'

// Cement India demo mode — cad.retaliq.net sirve MP4 industriales en loop
// desde /branding/cement-videos/cement-{1..4}.mp4 en lugar de negociar WHEP
// contra el sidecar M4 (que no existe en este tenant demo). Round-robin por
// número de canal. No aplica a los tenants productivos (Copec, Hites, etc).
const _isCementDemo = typeof window !== 'undefined' && window.location.hostname === 'cad.retaliq.net'
function _demoVideoForChannel(channel) {
  const idx = (((channel || 0) - 1) % 4 + 4) % 4 + 1
  return `/branding/cement-videos/cement-${idx}.mp4`
}

// Backoff exponencial para reconexión. Si ICE falla, no spammeamos el sidecar:
// esperamos cada vez más antes de reintentar. Esto también protege al M4 cuando
// está reiniciando — no recibe N intentos por segundo desde N cells.
const RECONNECT_DELAYS_MS = [5_000, 15_000, 45_000, 120_000, 300_000]

export default function WhepPlayer({
  siteId, channel, channelName, token, onFallback, compact = false,
  posterUrl = null,
}) {
  // ── Cement India demo — short-circuit: renderizar MP4 loop ─────────────
  // No hay sidecar M4 en este tenant. Servimos uno de los 4 videos
  // industriales en loop desde Caddy, mapeando por número de canal.
  if (_isCementDemo) {
    return (
      <div style={{ position: 'relative', width: '100%', height: '100%', background: '#000' }}>
        <video
          src={_demoVideoForChannel(channel)}
          autoPlay muted loop playsInline
          style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
        />
        <div style={{
          position: 'absolute', top: 8, left: 8, display: 'flex', alignItems: 'center', gap: 6,
          padding: '3px 8px', borderRadius: 4, background: 'rgba(0,0,0,0.65)',
          color: '#FFF', fontSize: 11, fontWeight: 700, letterSpacing: '0.06em',
        }}>
          <span style={{
            width: 8, height: 8, borderRadius: '50%', background: '#EF4444',
            display: 'inline-block', animation: 'pulseDot 1.5s ease-in-out infinite',
          }} />
          LIVE
        </div>
        {channelName && (
          <div style={{
            position: 'absolute', bottom: 8, left: 8, padding: '3px 8px',
            borderRadius: 4, background: 'rgba(0,0,0,0.6)', color: '#F1F5F9',
            fontSize: 11, fontWeight: 500,
          }}>
            {channelName}
          </div>
        )}
        <style>{`@keyframes pulseDot { 0%,100%{opacity:1} 50%{opacity:0.35} }`}</style>
      </div>
    )
  }

  const videoRef = useRef(null)
  const pcRef    = useRef(null)
  const sessRef  = useRef(null)
  const streamRef = useRef(null)   // MediaStream remoto — para cachear en pool
  const retryTimerRef = useRef(null)
  const attemptRef    = useRef(0)
  const [state, setState] = useState('connecting')   // connecting|playing|error|retrying
  const [err,   setErr]   = useState(null)
  const [retryIn, setRetryIn] = useState(0) // segundos hasta próximo reintento, para UI

  // Pool key: stream identificado por (sitio, canal). Sprint Performance Live.
  // Si el mismo (siteId, channel) ya tiene PC activo de un mount previo, lo
  // reusamos y switch es <100ms en lugar de 1.5-3s.
  const poolKey = siteId != null && channel != null
    ? `whep-${siteId}-${channel}` : null

  useEffect(() => {
    let cancelled = false
    let pc = null

    // ── Pool fast-path: si ya hay stream activo en pool para este (site,channel),
    // attach inmediato sin negotiate. <100ms. ────────────────────────────────────
    if (poolKey) {
      const hit = streamPool.acquire(poolKey)
      if (hit) {
        if (videoRef.current) videoRef.current.srcObject = hit.stream
        pc = hit.pc
        pcRef.current = pc
        streamRef.current = hit.stream
        setState('playing')
        setErr(null)
        attemptRef.current = 0
        // Re-attach handlers reactivos al state del componente.
        // El handler viejo apuntaba al setState del mount previo (ya unmount).
        pc.oniceconnectionstatechange = () => {
          const s = pc.iceConnectionState
          if (s === 'failed') scheduleReconnect('ICE failed (post-pool)')
        }
        pc.onconnectionstatechange = () => {
          if (pc.connectionState === 'failed' || pc.connectionState === 'closed') {
            if (!cancelled) {
              streamPool.drop(poolKey)
              scheduleReconnect(`pc ${pc.connectionState} (post-pool)`)
            }
          }
        }
        // Cleanup: release pool entry sin cerrar PC.
        return () => {
          cancelled = true
          if (retryTimerRef.current) clearTimeout(retryTimerRef.current)
          streamPool.release(poolKey)
        }
      }
    }

    function scheduleReconnect(reason) {
      if (cancelled) return
      // Cerramos el PC actual y planificamos un nuevo start()
      if (pc) { try { pc.close() } catch {} pc = null }
      pcRef.current = null
      const idx = Math.min(attemptRef.current, RECONNECT_DELAYS_MS.length - 1)
      const delayMs = RECONNECT_DELAYS_MS[idx]
      attemptRef.current += 1
      const tag = `[WHEP ch${channel}]`
      console.log(tag, `reconnect en ${delayMs/1000}s (intento ${attemptRef.current}, motivo: ${reason})`)
      setState('retrying')
      setErr(`Reintento ${attemptRef.current} en ${Math.round(delayMs/1000)}s — ${reason}`)
      // Countdown UI cada segundo
      let remaining = Math.round(delayMs / 1000)
      setRetryIn(remaining)
      const tick = setInterval(() => {
        remaining -= 1
        if (remaining <= 0) clearInterval(tick)
        else setRetryIn(remaining)
      }, 1000)
      retryTimerRef.current = setTimeout(() => {
        clearInterval(tick)
        if (cancelled) return
        start()
      }, delayMs)
    }

    async function start() {
      const tag = `[WHEP ch${channel}]`
      try {
        console.log(tag, 'start')
        setState('connecting')
        setErr(null)

        const gr = await fetch(
          `/api/streams/${siteId}/grant?channel=${channel}`,
          { method: 'POST', headers: { Authorization: `Bearer ${token}` } },
        )
        if (!gr.ok) throw new Error(`grant ${gr.status}: ${await gr.text()}`)
        const grant = await gr.json()
        console.log(tag, 'grant OK, ice_servers:', (grant.ice_servers || []).length)
        if (cancelled) return

        const iceServers = grant.ice_servers || []
        const policy = /webrtc_policy=relay/.test(window.location.search)
          ? 'relay' : 'all'
        pc = new RTCPeerConnection({
          iceServers,
          bundlePolicy: 'max-bundle',
          iceTransportPolicy: policy,
        })
        // pc.onconnectionstatechange se setea más abajo, junto con el handler
        // de reconnect (después del oniceconnectionstatechange).
        pcRef.current = pc
        pc.addTransceiver('video', { direction: 'recvonly' })
        pc.addTransceiver('audio', { direction: 'recvonly' })

        pc.ontrack = (e) => {
          if (e.streams[0]) {
            streamRef.current = e.streams[0]   // cachear para pool
            if (videoRef.current) videoRef.current.srcObject = e.streams[0]
          }
        }
        // ICE state machine: new → checking → connected → completed
        //                     ↑           ↓
        //                     disconnected (transient, puede recuperarse)
        //                     ↓
        //                     failed (terminal)
        //
        // Pre-2026-06-05: marcábamos error en 'disconnected' lo que rompía
        // antes que ICE terminara su trickle. El pair real conecta cuando
        // se le da tiempo. Solo 'failed' es terminal definitivo.
        pc.oniceconnectionstatechange = () => {
          const s = pc.iceConnectionState
          console.log(tag, 'ice →', s)
          if (s === 'connected' || s === 'completed') {
            // Conexión exitosa — reset contador de reintentos
            setState('playing')
            attemptRef.current = 0
            setErr(null)
            // Registrar en pool para reuso si el operador vuelve a esta cámara
            if (poolKey && streamRef.current && pc) {
              const capturedPc = pc
              const capturedStream = streamRef.current
              streamPool.put(poolKey, {
                pc: capturedPc,
                stream: capturedStream,
                close: () => { try { capturedPc.close() } catch {} },
              })
            }
          }
          else if (s === 'failed') {
            scheduleReconnect('ICE failed')
          }
          // 'disconnected' es transient — lo dejamos pasar. Si no recupera
          // en ~20s, el browser lo va a marcar como 'failed' solito y entramos
          // al reconnect.
        }
        pc.onconnectionstatechange = () => {
          console.log(tag, 'pc →', pc.connectionState)
          if (pc.connectionState === 'failed' || pc.connectionState === 'closed') {
            // Solo reagendamos si NO veníamos de un reset cancelado.
            if (!cancelled) scheduleReconnect(`pc ${pc.connectionState}`)
          }
        }

        const offer = await pc.createOffer()
        await pc.setLocalDescription(offer)

        // MediaMTX WHEP no soporta trickle ICE completo — los candidates que
        // no estén en el SDP inicial son ignorados. Hay que esperar a que el
        // browser termine de gather candidates (host + srflx + relay) antes
        // del POST. Sin esto, el SDP sale con solo host candidates → pair
        // selection falla → ICE → checking → disconnected → failed.
        //
        // Mi test standalone que sí conectó esperaba 3s con setTimeout. La
        // forma correcta es escuchar `icegatheringstate === complete`, con
        // timeout de 3s como red.
        await new Promise((resolve) => {
          if (pc.iceGatheringState === 'complete') return resolve()
          const onChange = () => {
            if (pc.iceGatheringState === 'complete') {
              pc.removeEventListener('icegatheringstatechange', onChange)
              resolve()
            }
          }
          pc.addEventListener('icegatheringstatechange', onChange)
          setTimeout(() => {
            pc.removeEventListener('icegatheringstatechange', onChange)
            resolve()
          }, 3000)
        })
        if (cancelled) return
        const finalSdp = pc.localDescription?.sdp || offer.sdp
        console.log(tag, 'gather complete, sdp candidates:',
          (finalSdp.match(/a=candidate:/g) || []).length)

        const ar = await fetch(grant.whep_url, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/sdp',
            'Authorization': `Bearer ${grant.stream_token}`,
          },
          body: finalSdp,
        })
        if (!ar.ok) throw new Error(`WHEP ${ar.status}`)
        const answerSdp = await ar.text()
        sessRef.current = {
          location_url : ar.headers.get('Location'),
          stream_token : grant.stream_token,
        }
        if (cancelled) return
        await pc.setRemoteDescription({ type: 'answer', sdp: answerSdp })
      } catch (e) {
        if (cancelled) return
        console.warn(`[WHEP ch${channel}] start error:`, e.message)
        // Cualquier error transitorio (grant 5xx, WHEP 502, red caída) → reconnect
        // con backoff. Solo si el error es permanente (401 sin token, 404 sitio
        // inexistente) deberíamos darnos por vencidos — los chequeamos por código.
        const msg = e.message || String(e)
        const permanent = /\b(401|403|404)\b/.test(msg)
        if (permanent) {
          setState('error')
          setErr(msg)
        } else {
          scheduleReconnect(msg.slice(0, 60))
        }
      }
    }
    start()

    return () => {
      cancelled = true
      if (retryTimerRef.current) {
        clearTimeout(retryTimerRef.current)
        retryTimerRef.current = null
      }
      // Sprint Performance Live: si el PC está sano y en el pool, NO cerrar —
      // dejarlo warm para próximo mount del mismo (siteId, channel). LRU del
      // pool decidirá cuándo cerrarlo. Si no está en pool (error/disconnect),
      // hacer cleanup completo como antes.
      const inPool = poolKey && streamPool.acquire(poolKey)
      if (inPool) {
        // acquire() incrementó refCount — devolvemos al pool con release.
        streamPool.release(poolKey)
        streamPool.release(poolKey)   // segundo release neutraliza el incremento
        // El cleanup PC + DELETE session se hace al evict por LRU.
        return
      }
      if (sessRef.current?.location_url && sessRef.current?.stream_token) {
        fetch(sessRef.current.location_url, {
          method: 'DELETE',
          headers: { Authorization: `Bearer ${sessRef.current.stream_token}` },
          keepalive: true,
        }).catch(() => {})
      }
      if (pc) { try { pc.close() } catch {} }
    }
  }, [siteId, channel, token])

  return (
    <div style={{
      flex: 1, position: 'relative', background: '#000',
      width: '100%', height: '100%',
      overflow: 'hidden', display: 'flex',
      alignItems: 'center', justifyContent: 'center', minHeight: 0,
    }}>
      <video
        ref={videoRef}
        autoPlay playsInline muted
        poster={posterUrl || undefined}
        style={{
          width: '100%', height: '100%',
          objectFit: 'contain', display: 'block',
        }}
      />
      {state === 'connecting' && (
        <div style={{
          position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.6)',
          display: 'flex', flexDirection: 'column',
          alignItems: 'center', justifyContent: 'center', gap: 8, color: '#e5e5e5',
        }}>
          <div style={{
            width: 22, height: 22, borderRadius: '50%',
            border: '2px solid rgba(255,255,255,0.25)', borderTopColor: '#fff',
            animation: 'mv-spin 0.8s linear infinite',
          }} />
          <span style={{ fontSize: 11 }}>Conectando WebRTC…</span>
        </div>
      )}
      {state === 'retrying' && (
        <div style={{
          position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.7)',
          display: 'flex', flexDirection: 'column',
          alignItems: 'center', justifyContent: 'center', gap: 6, color: '#e5e5e5',
        }}>
          <span style={{ fontSize: 12, fontWeight: 600 }}>Reconectando…</span>
          <span style={{ fontSize: 22, fontVariantNumeric: 'tabular-nums', fontWeight: 700, color: '#fbbf24' }}>
            {retryIn}s
          </span>
          {!compact && err && (
            <span style={{ fontSize: 10, color: '#aaa', maxWidth: '80%', textAlign: 'center' }}>{err}</span>
          )}
        </div>
      )}
      {state === 'error' && (
        <div style={{
          position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.7)',
          display: 'flex', flexDirection: 'column', gap: 8,
          alignItems: 'center', justifyContent: 'center', color: '#e5e5e5',
        }}>
          <span style={{ fontSize: 12 }}>WebRTC falló</span>
          {!compact && (
            <>
              <span style={{ fontSize: 10, color: '#aaa', maxWidth: '80%', textAlign: 'center' }}>
                {err}
              </span>
              {onFallback && (
                <button
                  onClick={onFallback}
                  style={{
                    padding: '5px 12px', fontSize: 10, fontWeight: 600,
                    background: 'var(--accent)', color: '#fff',
                    border: 'none', borderRadius: 4, cursor: 'pointer',
                  }}
                >Usar MJPEG vía cloud</button>
              )}
            </>
          )}
        </div>
      )}
      {channelName && (
        <div style={{
          position: 'absolute', bottom: 0, left: 0, right: 0,
          background: 'linear-gradient(to top, rgba(0,0,0,0.85), transparent)',
          padding: '14px 8px 5px 8px',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          gap: 6, pointerEvents: 'none',
        }}>
          <span style={{
            fontSize: 11, color: '#e5e5e5', fontWeight: 500,
            textShadow: '0 1px 3px rgba(0,0,0,0.8)',
            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
          }}>{channelName}</span>
          <span style={{
            fontSize: 9, fontWeight: 700,
            color: state === 'playing' ? '#10b981' : '#f59e0b',
            textShadow: '0 1px 3px rgba(0,0,0,0.8)',
            letterSpacing: '0.05em',
            flexShrink: 0,
          }}>● RTC {state === 'playing' ? 'DIRECT' : state.toUpperCase()}</span>
        </div>
      )}
    </div>
  )
}
